/**
 * MP → ORB conversion page (10 MP = 1 ORB).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MP_PER_UO } from '@uo/shared';
import { ApiError } from '../api/client';
import { usersAPI } from '../api/users';
import { useAuthStore } from '../store/auth';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedField,
  ThemedInput,
  ThemedNotice,
  ThemedPanel,
  ThemedSubmitButton,
  ThemedTextarea,
} from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';
import { tokenDisplayLabel } from '@/utils/orb-display';

function fmtNum(v: string | undefined, digits = 4): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

export function MpToOrbPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile);

  const [mpAmount, setMpAmount] = useState('');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [memo, setMemo] = useState('');
  const [showSecurity, setShowSecurity] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: preview } = useQuery({
    queryKey: ['users', 'convert', 'mp-uo', 'preview'],
    queryFn: () => usersAPI.convertMpUoPreview(),
    refetchInterval: 30_000,
  });

  const uoPreview = useMemo(() => {
    const mp = Number(mpAmount);
    if (!Number.isFinite(mp) || mp <= 0) return null;
    return (mp / MP_PER_UO).toFixed(6);
  }, [mpAmount]);

  const convertMut = useMutation({
    mutationFn: () =>
      usersAPI.convertMpUo({
        mp_amount: mpAmount.trim(),
        transaction_password: txPassword.trim(),
        security_answer: securityAnswer.trim(),
        memo: memo.trim() || undefined,
      }),
    onSuccess: (res) => {
      setSuccess(true);
      setError(null);
      qc.invalidateQueries({ queryKey: ['user'] });
      qc.invalidateQueries({ queryKey: ['users', 'convert', 'mp-uo'] });
      const data = res as { uo_credited?: string; mp_burned?: string };
      alert(
        t('mpConvert.successDetail', {
          mp: data.mp_burned ?? mpAmount,
          orb: data.uo_credited ?? uoPreview ?? '0',
        }),
      );
      setTimeout(() => navigate(-1), 800);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('mpConvert.failed'));
    },
  });

  const mpBalance = preview?.mp_balance ?? '0';
  const orbBalance = preview?.uo_balance ?? '0';
  const securityQuestion =
    ((profile as Record<string, unknown> | null)?.security_question as string | undefined) ??
    t('convert.defaultSecurityQuestion');

  const formDisabled =
    !mpAmount.trim() ||
    Number(mpAmount) <= 0 ||
    !txPassword.trim() ||
    (showSecurity && !securityAnswer.trim()) ||
    convertMut.isPending;

  function handleSubmit() {
    setError(null);
    if (!mpAmount || Number(mpAmount) <= 0) {
      setError(t('mpConvert.amountError'));
      return;
    }
    if (Number(mpAmount) > Number(mpBalance)) {
      setError(t('mpConvert.insufficientMp'));
      return;
    }
    convertMut.mutate();
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('mpConvert.title')} onBack={() => navigate(-1)} theme={C} />
        <div
          className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]"
          style={{ height: 'calc(100% - 70px)', paddingBottom: '88px' }}
        >
          <ThemedPanel className="overflow-hidden p-0">
            <div className="relative px-4 py-4">
              <div
                className="pointer-events-none absolute inset-x-0 top-0 h-px"
                style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold} 50%, transparent 100%)` }}
              />
              <div className="text-center text-xs font-medium" style={{ color: C.muted }}>
                {t('mpConvert.rateHint')}
              </div>
              <div className="mt-2 text-center font-mono text-2xl font-bold" style={{ color: C.gold }}>
                10 MP = 1 {tokenDisplayLabel('uo')}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.softBorder, background: C.inputBg }}>
                  <div className="text-[10px]" style={{ color: C.muted }}>{t('mpConvert.mpBalance')}</div>
                  <div className="mt-1 font-mono text-lg font-semibold" style={{ color: C.text }}>
                    {fmtNum(mpBalance)}
                  </div>
                </div>
                <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.softBorder, background: C.inputBg }}>
                  <div className="text-[10px]" style={{ color: C.muted }}>{t('mpConvert.orbBalance')}</div>
                  <div className="mt-1 font-mono text-lg font-semibold" style={{ color: C.gold }}>
                    {fmtNum(orbBalance)}
                  </div>
                </div>
              </div>
            </div>
          </ThemedPanel>

          {success && <ThemedNotice tone="success">{t('mpConvert.success')}</ThemedNotice>}
          {error && <ThemedNotice tone="error">{error}</ThemedNotice>}

          <ThemedPanel className="p-4">
            <div className="flex flex-col gap-5">
              <ThemedField label={t('mpConvert.mpAmount')} required>
                <div className="relative">
                  <ThemedInput
                    type="number"
                    value={mpAmount}
                    onChange={(e) => setMpAmount(e.target.value)}
                    placeholder={t('mpConvert.placeholder')}
                    min="0"
                    step="any"
                    className="pr-16"
                  />
                  <button
                    type="button"
                    onClick={() => setMpAmount(mpBalance)}
                    className="absolute right-2 top-1/2 flex h-7 -translate-y-1/2 items-center px-2 text-[11px] font-semibold"
                    style={{ color: C.gold }}
                  >
                    {t('common.all_btn')}
                  </button>
                </div>
              </ThemedField>

              <ThemedField label={t('mpConvert.orbReceive')}>
                <div
                  className="flex h-10 items-center rounded-lg border px-3 font-mono text-base font-bold"
                  style={{ borderColor: C.softBorder, background: 'rgba(0,0,0,0.24)', color: C.gold }}
                >
                  {uoPreview ?? '—'}
                </div>
              </ThemedField>

              <ThemedField label={t('convert.memo')}>
                <ThemedTextarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={2} />
              </ThemedField>

              <TransactionSecurity
                txPassword={txPassword}
                onTxPasswordChange={setTxPassword}
                showSecurity={showSecurity}
                onShowSecurityChange={setShowSecurity}
                securityAnswer={securityAnswer}
                onSecurityAnswerChange={setSecurityAnswer}
                securityQuestionText={securityQuestion}
              />
            </div>
          </ThemedPanel>
        </div>

        <ThemedBottomActionBar>
          <ThemedSubmitButton
            disabled={formDisabled || convertMut.isPending}
            onClick={handleSubmit}
          >
            {t('mpConvert.submit')}
          </ThemedSubmitButton>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}
